import React, { useEffect, useMemo, useState } from "react";
import { App, Button, Card, Input, Space, Switch, Tabs, Typography } from "antd";
import { fetchAgentSiteSettings, saveAgentSiteSettings } from "../../services/agentSiteSettingsApi";
import PublicSiteCommissionsSettings from "../../Components/Agent/SiteSettings/PublicSiteCommissionsSettings";
import HubwayzHotelSaleCommissionSettings from "../../Components/Agent/SiteSettings/HubwayzHotelSaleCommissionSettings";
import AgentSiteSliderSettings from "../../Components/Agent/SiteSettings/AgentSiteSliderSettings";
import AgentSiteDestinationsSettings from "../../Components/Agent/SiteSettings/AgentSiteDestinationsSettings";
import AgentWhiteLabelDomainSettings from "../../Components/Agent/SiteSettings/AgentWhiteLabelDomainSettings";
import { useAgentEntitlements } from "../../features/agentEntitlements/useAgentEntitlements";

const { Text, Title } = Typography;
const { TextArea } = Input;

export default function SiteSettingsPage() {
  const { message } = App.useApp();
  const { canUseAgentPublicSite } = useAgentEntitlements();
  const showPublicSiteCommissionsTab = Boolean(canUseAgentPublicSite);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [siteName, setSiteName] = useState("");
  const [siteTagline, setSiteTagline] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [ctaText, setCtaText] = useState("");

  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");

  const [aboutTitle, setAboutTitle] = useState("");
  const [aboutText, setAboutText] = useState("");
  const [showAboutSection, setShowAboutSection] = useState(true);

  const [phone, setPhone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchAgentSiteSettings();
        if (cancelled) return;
        setSiteName(data?.site_name || "");
        setSiteTagline(data?.site_tagline || "");
        setPrimaryColor(data?.primary_color || "");
        setCtaText(data?.cta_text || "");
        setMetaTitle(data?.meta_title || "");
        setMetaDescription(data?.meta_description || "");
        setMetaKeywords(data?.meta_keywords || "");
        setAboutTitle(data?.about_title || "");
        setAboutText(data?.about_text || "");
        setShowAboutSection(data?.show_about_section !== false);
        setPhone(data?.phone || "");
        setWhatsapp(data?.whatsapp || "");
        setEmail(data?.email || "");
        setAddress(data?.address || "");
        setFacebookUrl(data?.facebook_url || "");
        setInstagramUrl(data?.instagram_url || "");
      } catch (e) {
        if (!cancelled) {
          message.error(e?.response?.data?.message || "טעינת הגדרות האתר נכשלה");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [message]);

  const googlePreviewTitle = useMemo(() => {
    const v = String(metaTitle || "").trim();
    return v || "כותרת תוצאות החיפוש שלך";
  }, [metaTitle]);

  const googlePreviewDescription = useMemo(() => {
    const v = String(metaDescription || "").trim();
    return v || "תיאור הדף שלך יופיע כאן כאשר תגדירו meta description.";
  }, [metaDescription]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAgentSiteSettings({
        site_name: siteName,
        site_tagline: siteTagline,
        primary_color: primaryColor,
        cta_text: ctaText,
        meta_title: metaTitle,
        meta_description: metaDescription,
        meta_keywords: metaKeywords,
        about_title: aboutTitle,
        about_text: aboutText,
        show_about_section: showAboutSection,
        phone,
        whatsapp,
        email,
        address,
        facebook_url: facebookUrl,
        instagram_url: instagramUrl,
      });
      message.success("הגדרות האתר נשמרו בהצלחה");
    } catch (e) {
      message.error(e?.response?.data?.message || "שמירת הגדרות האתר נכשלה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div dir="rtl" style={{ width: "100%" }}>
      <Card loading={loading}>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Title level={3} style={{ margin: 0 }}>
            הגדרות האתר שלי
          </Title>

          <Tabs
            defaultActiveKey="general"
            items={[
              {
                key: "general",
                label: "כללי",
                children: (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Input
                      placeholder="שם האתר"
                      value={siteName}
                      onChange={(e) => setSiteName(e.target.value)}
                    />
                    <Input
                      placeholder="סלוגן"
                      value={siteTagline}
                      onChange={(e) => setSiteTagline(e.target.value)}
                    />
                    <Input
                      placeholder="צבע ראשי (למשל #ff3703)"
                      value={primaryColor}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                    />
                    <Input
                      placeholder="טקסט כפתור לידים"
                      value={ctaText}
                      onChange={(e) => setCtaText(e.target.value)}
                    />
                  </Space>
                ),
              },
              {
                key: "seo",
                label: "SEO",
                children: (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Input
                      placeholder="Meta title"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                    />
                    <TextArea
                      rows={3}
                      placeholder="Meta description"
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                    />
                    <Input
                      placeholder="Meta keywords (comma separated)"
                      value={metaKeywords}
                      onChange={(e) => setMetaKeywords(e.target.value)}
                    />
                    <Card size="small" title="תצוגה מקדימה בגוגל">
                      <Title level={5} style={{ color: "#1a0dab", marginBottom: 4 }}>
                        {googlePreviewTitle}
                      </Title>
                      <Text type="secondary">{googlePreviewDescription}</Text>
                    </Card>
                  </Space>
                ),
              },
              {
                key: "about",
                label: "אודות הסוכנות",
                children: (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Input
                      placeholder="כותרת אודות"
                      value={aboutTitle}
                      onChange={(e) => setAboutTitle(e.target.value)}
                    />
                    <TextArea
                      rows={8}
                      placeholder="תוכן אודות"
                      value={aboutText}
                      onChange={(e) => setAboutText(e.target.value)}
                    />
                    <Space>
                      <Switch
                        checked={showAboutSection}
                        onChange={setShowAboutSection}
                      />
                      <Text>להציג אזור אודות באתר הציבורי</Text>
                    </Space>
                  </Space>
                ),
              },
              {
                key: "contact",
                label: "פרטי קשר",
                children: (
                  <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Input placeholder="טלפון" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    <Input
                      placeholder="וואטסאפ"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                    <Input placeholder="אימייל" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <Input placeholder="כתובת" value={address} onChange={(e) => setAddress(e.target.value)} />
                    <Input
                      placeholder="Facebook URL"
                      value={facebookUrl}
                      onChange={(e) => setFacebookUrl(e.target.value)}
                    />
                    <Input
                      placeholder="Instagram URL"
                      value={instagramUrl}
                      onChange={(e) => setInstagramUrl(e.target.value)}
                    />
                  </Space>
                ),
              },
              {
                key: "white-label-domain",
                label: "דומיין מותאם",
                children: <AgentWhiteLabelDomainSettings />,
              },
              {
                key: "slider",
                label: "סליידר תמונות",
                children: <AgentSiteSliderSettings />,
              },
              {
                key: "site-destinations",
                label: "יעדים באתר שלי",
                children: <AgentSiteDestinationsSettings />,
              },
              ...(showPublicSiteCommissionsTab
                ? [
                    {
                      key: "commissions",
                      label: "עמלות",
                      children: (
                        <>
                          <HubwayzHotelSaleCommissionSettings />
                          <PublicSiteCommissionsSettings />
                        </>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          <Button type="primary" loading={saving} onClick={handleSave}>
            שמור הגדרות האתר
          </Button>
        </Space>
      </Card>
    </div>
  );
}
